import type { NavId } from "$lib/nav/routes"
import { cn } from "@tastyworks/ui-library"
import { cva } from "class-variance-authority"

/**
 * Represents the the first level of the side navigation menu and
 * must have a list of items.
 * - For example, Personal Info
 */
export type NavGroup = {
  href: string
  id: NavId
  items: Array<NavItem>
  hidden?: boolean
}

/**
 * Represents a nested level of the side navigation menu.
 * Only the first 2 levels are displayed on the side navigation menu (group and section)
 * - For example, Contact Information (nested under Personal Info)
 * - Additional levels (3+) continue to have dedicated URLs
 */
export type NavItem = {
  href: string
  id: NavId
  items?: Array<NavItem>
  hidden?: boolean
}

/**
 * Represents a step/page displayed to the user. A culmination of a leaf
 * NavItem and all direct ancestors.
 * - For example, Name (nested under Contact Information and Personal Info)
 */
export type NavStep = {
  href: string
  id: string
  hidden?: boolean
}

/**
 * - disabled - not clickable; groups and sections that have not been reached yet
 * - in-progress - furthest reached group and section
 * - enabled - clickable; already completed groups and sections
 * - review - completed all forms
 */
export type NavItemState =
  | "enabled"
  | "disabled"
  | "in-progress"
  | "review"
  | "immutable"

export class StepNode {
  value: NavStep
  next: StepNode | null = null
  prev: StepNode | null = null
  index: number
  groupIndex: number

  constructor(value: NavStep, index: number, groupIndex: number) {
    this.value = value
    this.index = index
    this.groupIndex = groupIndex
  }
}
export class LinkedList {
  head: StepNode | null = null
  tail: StepNode | null = null

  push(value: NavStep, index: number, groupIndex: number): void {
    const newNode = new StepNode(value, index, groupIndex)

    if (!this.head) {
      this.head = newNode
      this.tail = newNode
    } else {
      newNode.prev = this.tail
      this.tail!.next = newNode
      this.tail = newNode
    }
  }

  *forward(): Generator<StepNode, void, unknown> {
    let current: StepNode | null = this.head
    while (current) {
      yield current
      current = current.next
    }
  }

  *backward(): Generator<StepNode, void, unknown> {
    let current = this.tail
    while (current) {
      yield current
      current = current.prev
    }
  }
}

export function buildNavSteps(navGroups: Array<NavGroup>) {
  const list = new LinkedList()

  let index = 0
  let groupIndex = 0

  function buildLinkedList(curItem: NavItem, path: string, id: string) {
    if (curItem.hidden) return

    if (!curItem.items) {
      const newStep: NavStep = {
        id: id ? [id, curItem.id].join(".") : curItem.id,
        href: path + curItem.href,
        hidden: curItem.hidden,
      }

      list.push(newStep, index++, groupIndex)
      return
    }

    for (let i = 0; i < curItem.items.length; ++i) {
      buildLinkedList(
        curItem.items[i],
        path + curItem.href,
        id ? [id, curItem.id].join(".") : curItem.id
      )
    }

    if (path === "") ++groupIndex
  }

  buildLinkedList(
    {
      id: "" as NavId,
      href: "",
      items: navGroups,
    },
    "",
    ""
  )

  return list
}

const ACTIVE_STYLE = [
  "before:border-icon-navigation-active",
  "before:-z-10",
  "before:bg-general-surface-01",
  "before:w-3",
  "before:h-3",
  "before:rounded-full",
  "before:absolute",
  "before:border",
  "before:absolute-center",
]
const ENABLED_STYLE =
  "bg-[var(--color-icon-navigation-active)] text-general-inverted-text"

const DISABLED_STYLE =
  "border-icon-navigation-inactive bg-general-body text-navigation-page-label-inactive"

const IMMUTABLE_STYLE =
  "border-[var(--color-text-icon-general-disabled)] bg-[var(--color-text-icon-general-disabled)] text-[var(--color-background-general-surface-01)]"

const circleVariants = cva("rounded-full border border-transparent", {
  variants: {
    size: {
      small: "h-2 w-2",
      large: "flex h-6 w-6 items-center justify-center",
    },
    state: {
      enabled: ENABLED_STYLE,
      complete: ENABLED_STYLE,
      review: ENABLED_STYLE,
      disabled: DISABLED_STYLE,
      immutable: IMMUTABLE_STYLE,
      "in-progress":
        "border-icon-navigation-active bg-general-body text-icon-general-primary",
    },
    active: {
      true: ACTIVE_STYLE,
      false: "z-10",
    },
  },
  compoundVariants: [
    {
      state: "in-progress",
      size: "small",
    },
  ],
})

type CircleVariants = Parameters<typeof circleVariants>[0]
export const circle = (variants: CircleVariants) => cn(circleVariants(variants))

const textVariants = cva("transition-colors", {
  variants: {
    size: {
      small: "font-medium-500 md:font-small-500",
      large: "font-large-500 md:font-medium-600",
    },
    state: {
      enabled: "text-input-fields-input-active",
      disabled: "text-navigation-page-label-inactive",
      immutable: "text-navigation-page-label-inactive",
      active: "text-navigation-page-label-active",
      "in-progress": "text-input-fields-input-active",
      review: "text-input-fields-input-active",
      complete: "text-input-fields-input-active",
    },
  },
})

type TextVariants = Parameters<typeof textVariants>[0]
export const text = (variants: TextVariants) => cn(textVariants(variants))

const lineVariants = cva(`absolute-center-x absolute h-10 w-px origin-top`, {
  variants: {
    size: {
      small: "bottom-1/2 -z-20",
      large: "top-full h-4",
    },
    disabled: {
      false: "bg-[var(--color-icon-navigation-active)]",
      true: "bg-[var(--color-icon-navigation-inactive)]",
    },
  },
})

type LineVariants = Parameters<typeof lineVariants>[0]
export const line = (variants: LineVariants) => cn(lineVariants(variants))
